/* eslint-disable @typescript-eslint/no-invalid-this */

import ReactDOM from 'react-dom';
import accessLevelDropdownBridge from '../access-level-dropdown-bridge/access-level-dropdown-bridge';
import { RenderWithWrapper } from '../../../../../global/global.react';
import { AccessLevelModal } from '../../../../../modules/teleconsent/components/access-level-modal';
import { RemoveFromActiveModal } from '../../../../../modules/teleconsent/components/remove-from-active';
import { MembershipType, UserType } from '../../../../../store/features/system';
import { AccessLevel } from '../../../../../modules/teleconsent/types';
import {
  hasGrowthBookFeature,
  GrowthBookFeatures,
} from '../../../../../modules/core';

(function () {
  'use strict';

  angular
    .module('doxy.realtime.components.teleconsent.manager.list.new', [
      'xeditable',
      accessLevelDropdownBridge,
    ])
    .run(function (editableOptions, editableThemes) {
      editableThemes.bs3.inputClass = 'input-sm';
      editableThemes.bs3.buttonsClass = 'btn-xs';
      editableOptions.theme = 'bs3';
    })
    .controller(
      'TCManagerListPageController',
      function ($scope, $stateParams, user, currentInstitution) {
        $scope.user = user;
        $scope.institution = Boolean($stateParams.institutionId)
          ? { id: $stateParams.institutionId }
          : currentInstitution;
      }
    )
    .directive('dTeleconsentManagerListNew', function () {
      return {
        restrict: 'E',
        scope: {
          institution: '=dInstitution',
          user: '=dUser',
        },
        templateUrl:
          '/app/realtime/components/teleconsent/manager/list/' +
          'teleconsent.manager.list.tpl.html',
        controller: 'TCManagerListControllerNew as vm',
      };
    })
    .controller(
      'TCManagerListControllerNew',
      function (
        $modal,
        $scope,
        $rootScope,
        LoopBackAuth,
        notificationFactory,
        ActiveDocument,
        TeleconsentDocument
      ) {
        const reactContainer = document.querySelector(
          '#teleconsent-access-level-modal'
        );

        const currentUser = LoopBackAuth.currentUserData;

        const originalDocuments = {};
        const isClinicOwnerOrAdmin =
          [MembershipType.owner, MembershipType.admin].includes(
            currentUser.institutionMember?.role
          ) &&
          Number(currentUser.institutionMember.institutionId) ===
            Number($scope.institution.id);
        const couldManageAccessLevel =
          isClinicOwnerOrAdmin || currentUser.type === UserType.admin;
        this.showAccessColumn = couldManageAccessLevel;
        this.isAccessLevelBannerVisible =
          this.showAccessColumn &&
          !localStorage.getItem('access-level-banner-hidden');

        this.closeAccessLevelBanner = () => {
          this.isAccessLevelBannerVisible = false;
          localStorage.setItem('access-level-banner-hidden', true);
        };

        const logAndShowError = (error) => {
          console.error(error);
          notificationFactory.error(error);
        };

        const fetchTeleconsents = async () => {
          $scope.loader = true;
          try {
            const personalDocuments = await ActiveDocument.getAvailable({
              institutionId: $scope.institution.id,
              userId: $scope.user.id,
            }).$promise;

            const personalDocumentsArr = personalDocuments
              .map((personalDoc) => ({
                ...personalDoc,
                accessLevel: Boolean(personalDoc.userId)
                  ? AccessLevel.only
                  : AccessLevel.all,
              }))
              .sort(
                (a, b) =>
                  new Date(a.created).getTime() - new Date(b.created).getTime()
              );

            originalDocuments.personal = personalDocumentsArr;
            originalDocuments.institution =
              await TeleconsentDocument.getForInstitution({
                institutionId: $scope.institution.id,
              }).$promise;

            this.documents = originalDocuments;
          } catch (error) {
            logAndShowError(error);
          } finally {
            // eslint-disable-next-line require-atomic-updates
            $scope.loader = false;
            $rootScope.$evalAsync();
          }
        };

        void fetchTeleconsents();

        const addActiveDocument = async (document) => {
          $scope.loader = true;

          try {
            await ActiveDocument.toggleForProvider({
              institutionId: $scope.institution.id,
              teleconsentDocumentId: document.id,
              isActive: true,
            }).$promise;

            await notificationFactory.showCommon(
              'success',
              'teleconsent.list.added-to-personal'
            );
          } catch (error) {
            logAndShowError(error);
          } finally {
            await fetchTeleconsents();
          }
        };

        const addPersonal = async (document) => {
          $scope.loader = true;

          try {
            ReactDOM.unmountComponentAtNode(reactContainer);
            await addActiveDocument(document);
          } catch (error) {
            logAndShowError(error);
          } finally {
            $scope.loader = false;
          }
        };

        const addToAllClinicMembers = async (document) => {
          $scope.loader = true;

          try {
            ReactDOM.unmountComponentAtNode(reactContainer);

            await ActiveDocument.toggleForClinic({
              institutionId: $scope.institution.id,
              teleconsentDocumentId: document.id,
              isActive: true,
            }).$promise;

            await notificationFactory.showCommon(
              'success',
              'teleconsent.list.added-to-all-clinic'
            );
          } catch (error) {
            logAndShowError(error);
          } finally {
            await fetchTeleconsents();
          }
        };

        const openAddModal = (document) => {
          RenderWithWrapper(
            AccessLevelModal,
            {
              isOpen: true,
              addToAllClinicMembers: async () =>
                addToAllClinicMembers(document),
              addToPersonal: async () => addPersonal(document),
              onClose: () => {
                ReactDOM.unmountComponentAtNode(reactContainer);
              },
            },
            reactContainer,
            $scope
          );
        };

        const removeFromClinicActive = async (document) => {
          $scope.loader = true;

          try {
            ReactDOM.unmountComponentAtNode(reactContainer);

            await ActiveDocument.toggleForClinic({
              institutionId: $scope.institution.id,
              teleconsentDocumentId: document.id,
              isActive: false,
            }).$promise;

            await notificationFactory.showCommon(
              'success',
              'teleconsent.list.remove-from-active'
            );
          } catch (error) {
            logAndShowError(error);
          } finally {
            await fetchTeleconsents();
            // eslint-disable-next-line require-atomic-updates
            $scope.loader = false;
          }
        };

        const openRemoveModal = (document) => {
          RenderWithWrapper(
            RemoveFromActiveModal,
            {
              isOpen: true,
              onCancel: () => {
                ReactDOM.unmountComponentAtNode(reactContainer);
              },
              onRemove: async () => removeFromClinicActive(document),
            },
            reactContainer,
            $scope
          );
        };

        const removeFromActiveDocuments = async (document) => {
          $scope.loader = true;

          try {
            await ActiveDocument.toggleForProvider({
              teleconsentDocumentId: document.id,
              institutionId: $scope.institution.id,
              isActive: false,
            }).$promise;

            await notificationFactory.showCommon(
              'success',
              'teleconsent.list.remove-from-active'
            );
          } catch (error) {
            logAndShowError(error);
          } finally {
            await fetchTeleconsents();
          }
        };

        angular.extend(this, {
          user: LoopBackAuth.currentUserData,
          documents: { personal: [], institution: [], global: [] },
          handleAccessLevelChange: async (accessLevel, document) => {
            if (accessLevel === AccessLevel.all) {
              await ActiveDocument.toggleForClinic({
                institutionId: $scope.institution.id,
                teleconsentDocumentId: document.id,
                isActive: true,
              }).$promise;
            } else {
              await ActiveDocument.toggleForClinic({
                institutionId: $scope.institution.id,
                teleconsentDocumentId: document.id,
                isActive: false,
              }).$promise;

              await ActiveDocument.toggleForProvider({
                institutionId: $scope.institution.id,
                teleconsentDocumentId: document.id,
                isActive: true,
              }).$promise;
            }

            await notificationFactory.showCommon(
              'success',
              accessLevel === AccessLevel.all
                ? 'teleconsent.list.added-to-all-clinic'
                : 'teleconsent.list.added-to-personal'
            );
            await fetchTeleconsents();
          },
          preview: async function (document) {
            $scope.loader = true;

            try {
              const fullDocument = await TeleconsentDocument.findById({
                id: document.id,
              }).$promise;

              $scope.selectedDocument = angular.copy(fullDocument);
            } catch (error) {
              await notificationFactory.showCommon(
                'error',
                'teleconsent.list.download-document-error'
              );
              console.error(error);
            } finally {
              $scope.loader = false;
            }

            $modal({
              scope: $scope,
              placement: '',
              contentTemplate:
                '/app/realtime/components/teleconsent/action/preview/' +
                `teleconsent.action.preview-paged.tpl.html`,
              template:
                '/app/realtime/components/teleconsent/action/share/' +
                'teleconsent.action.share.modal-template.tpl.html',
            });
          },

          addToPersonal: async (document) => {
            $scope.loader = true;

            if (couldManageAccessLevel) {
              openAddModal(document);
            } else {
              await addActiveDocument(document);
            }
            $scope.loader = false;
          },

          addToTrash: async (document) => {
            $scope.loader = true;

            try {
              await TeleconsentDocument.deleteById({ id: document.id })
                .$promise;

              this.documents.institution = this.documents.institution.filter(
                (institutionDocument) => institutionDocument.id !== document.id
              );
            } catch (error) {
              logAndShowError(error);
            } finally {
              $scope.loader = false;
              $rootScope.$evalAsync();
            }
          },

          updateName: async (data, document) => {
            if (Boolean(document.id)) {
              try {
                await TeleconsentDocument.prototype$updateAttributes(
                  {
                    id: document.id,
                  },
                  {
                    name: data,
                  }
                ).$promise;
              } catch (error) {
                logAndShowError(error);
              }
            }
          },

          removeFromPersonal: async (document) => {
            $scope.loader = true;

            if (
              couldManageAccessLevel &&
              document.accessLevel === AccessLevel.all
            ) {
              openRemoveModal(document);
            } else {
              await removeFromActiveDocuments(document);
            }

            $scope.loader = false;
          },
        });
      }
    );
})();
